import {
  App as AntdApp,
  Button,
  Card,
  Divider,
  Space,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { SafetyOutlined, DownloadOutlined, TeamOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface DataProcessor {
  id: number
  name: string
  purpose: string
  data_categories: string
  country: string
  avv_url?: string | null
}

interface DSGVORequest {
  id: string
  kind: 'export' | 'erasure'
  status: 'pending' | 'processing' | 'done' | 'failed'
  artifact_url: string | null
  requested_at: string
  completed_at: string | null
}

const statusColor: Record<string, string> = {
  pending: 'default',
  processing: 'blue',
  done: 'green',
  failed: 'red',
}

export default function PortalDatenschutz() {
  const { t } = useTranslation()
  const { message, modal } = AntdApp.useApp()
  const qc = useQueryClient()

  const processorsQ = useQuery({
    queryKey: ['portal-data-processors'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: DataProcessor[] }>('/api/v1/portal/data-processors')
      return res.data.data || []
    },
    staleTime: 300_000,
  })

  const listQ = useQuery({
    queryKey: ['portal-dsgvo-requests'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: DSGVORequest[] }>(
        '/api/v1/portal/dsgvo-requests',
      )
      return res.data.data
    },
    refetchInterval: 5000,
  })

  const createReq = useMutation({
    mutationFn: async (kind: 'export' | 'erasure') => {
      await apiClient.post('/api/v1/portal/dsgvo-requests', { kind })
    },
    onSuccess: (_d, kind) => {
      message.success(
        kind === 'export'
          ? t('portal.dsgvo.exportRequested', 'Export wurde angefordert.')
          : t('portal.dsgvo.erasureRequested', 'Löschung wurde angefordert.'),
      )
      qc.invalidateQueries({ queryKey: ['portal-dsgvo-requests'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.error', 'Anfrage fehlgeschlagen')),
  })

  const statusLabels: Record<string, string> = {
    pending: t('portalDatenschutz.statusPending', 'Ausstehend'),
    processing: t('portalDatenschutz.statusProcessing', 'In Arbeit'),
    done: t('portalDatenschutz.statusDone', 'Erledigt'),
    failed: t('portalDatenschutz.statusFailed', 'Fehler'),
  }
  const kindLabels: Record<string, string> = {
    export: t('portalDatenschutz.kindExport', 'Datenexport'),
    erasure: t('portalDatenschutz.kindErasure', 'Löschung'),
  }

  const renderMobileCard = (r: DSGVORequest) => (
    <div className={`mob-card${r.status === 'failed' ? ' mob-card--urgent' : ''}`}>
      <div className="mob-row">
        <Tag color={r.kind === 'export' ? 'blue' : 'red'}>{kindLabels[r.kind] || r.kind}</Tag>
        <Tag color={statusColor[r.status]}>{statusLabels[r.status] || r.status}</Tag>
      </div>
      <div className="flex-between">
        <Typography.Text type="secondary" className="text-sm">
          {new Date(r.requested_at).toLocaleString('de-DE')}
        </Typography.Text>
        {r.kind === 'export' && r.status === 'done' && r.artifact_url && (
          <Button size="small" type="primary" icon={<DownloadOutlined />}
            onClick={() => { window.location.href = `/api/v1/portal/dsgvo-requests/${r.id}/download` }}>
            {t('portalDatenschutz.download', 'Download')}
          </Button>
        )}
      </div>
    </div>
  )

  const columns: ColumnsType<DSGVORequest> = [
    {
      title: t('portal.dsgvo.columns.kind', 'Art'),
      dataIndex: 'kind',
      key: 'kind',
      width: 160,
      render: (v: string) => (
        <Tag color={v === 'export' ? 'blue' : 'red'}>
          {t(`portal.dsgvo.kind.${v}`, v)}
        </Tag>
      ),
    },
    {
      title: t('portal.dsgvo.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v: string) => (
        <Tag color={statusColor[v]}>{t(`portal.dsgvo.status.${v}`, v)}</Tag>
      ),
    },
    {
      title: t('portal.dsgvo.columns.requestedAt', 'Angefordert'),
      dataIndex: 'requested_at',
      key: 'requested_at',
      width: 170,
      render: (v: string) => new Date(v).toLocaleString('de-DE'),
    },
    {
      title: t('portal.dsgvo.columns.actions', 'Aktion'),
      key: 'actions',
      render: (_: unknown, r) =>
        r.kind === 'export' && r.status === 'done' && r.artifact_url ? (
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => {
              window.location.href = `/api/v1/portal/dsgvo-requests/${r.id}/download`
            }}
          >
            {t('portal.dsgvo.download', 'Download')}
          </Button>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
  ]

  return (
    <>
      <PageHeader
        icon={<SafetyOutlined />}
        title={t('portal.dsgvo.title', 'Datenschutz')}
        subtitle={t(
          'portal.dsgvo.subtitle',
          'Ihre DSGVO-Rechte: Auskunft über gespeicherte Daten (Art. 15) und Löschung (Art. 17).',
        )}
      />
      <Card className="content-card mb-16">
        <Space wrap>
          <Button
            type="primary"
            loading={createReq.isPending}
            onClick={() => createReq.mutate('export')}
          >
            {t('portal.dsgvo.requestExport', 'Datenexport anfordern (Art. 15)')}
          </Button>
          <Button
            danger
            loading={createReq.isPending}
            onClick={() =>
              modal.confirm({
                title: t('portal.dsgvo.erasureConfirmTitle', 'Konto löschen?'),
                content: t(
                  'portal.dsgvo.erasureConfirmText',
                  'Mit der Löschung werden Ihre personenbezogenen Daten pseudonymisiert. Rechnungs- und Buchhaltungs-Pflichtdaten bleiben aus gesetzlichen Gründen erhalten. Die Aktion ist nicht reversibel — Sie können sich danach nicht mehr im Portal anmelden.',
                ),
                okText: t('portal.dsgvo.erasureConfirm', 'Löschung anfordern'),
                okButtonProps: { danger: true },
                onOk: () => createReq.mutate('erasure'),
              })
            }
          >
            {t('portal.dsgvo.requestErasure', 'Löschung anfordern (Art. 17)')}
          </Button>
        </Space>
      </Card>
      {(() => {
        const reqs = listQ.data || []
        const pending = reqs.filter(r => r.status === 'pending' || r.status === 'processing')
        const exports = reqs.filter(r => r.kind === 'export')
        const erasures = reqs.filter(r => r.kind === 'erasure')
        return (
          <div className="kpi-grid kpi-grid-4 mb-16">
            <div className="kpi-tile">
              <div className="kpi-tile-label"><SafetyOutlined /> {t('portal.dsgvo.kpi.total', 'Anfragen')}</div>
              <div className="kpi-tile-value">{reqs.length}</div>
              <div className="kpi-tile-sub">{t('portal.dsgvo.kpi.total_sub', 'gesamt')}</div>
            </div>
            <div className={`kpi-tile ${pending.length > 0 ? 'kpi-warning' : ''}`}>
              <div className="kpi-tile-label">{t('portal.dsgvo.kpi.pending', 'In Bearbeitung')}</div>
              <div className={`kpi-tile-value ${pending.length > 0 ? 'kpi-warning' : ''}`}>{pending.length}</div>
              <div className="kpi-tile-sub">{t('portal.dsgvo.kpi.pending_sub', 'offen')}</div>
            </div>
            <div className="kpi-tile kpi-info">
              <div className="kpi-tile-label">{t('portal.dsgvo.kpi.exports', 'Auskunft')}</div>
              <div className="kpi-tile-value kpi-info">{exports.length}</div>
              <div className="kpi-tile-sub">{t('portal.dsgvo.kpi.exports_sub', 'Art. 15')}</div>
            </div>
            <div className="kpi-tile">
              <div className="kpi-tile-label">{t('portal.dsgvo.kpi.erasures', 'Löschung')}</div>
              <div className="kpi-tile-value">{erasures.length}</div>
              <div className="kpi-tile-sub">{t('portal.dsgvo.kpi.erasures_sub', 'Art. 17')}</div>
            </div>
          </div>
        )
      })()}
      <Card className="content-card mb-16">
        <DataTable<DSGVORequest>
          rowKey="id"
          columns={columns}
          dataSource={listQ.data || []}
          loading={listQ.isLoading}
          renderMobileCard={renderMobileCard}
        />
      </Card>

      {(processorsQ.data || []).length > 0 && (
        <Card className="content-card"
          title={
            <Space>
              <TeamOutlined />
              {t('portal.dsgvo.processors.title', 'Datenverarbeiter (Art. 13/14 DSGVO)')}
            </Space>
          }>
          <Typography.Text type="secondary" className="d-block mb-12">
            {t('portal.dsgvo.processors.hint',
              'Folgende Dienstleister verarbeiten personenbezogene Daten im Auftrag von NetCell IT.')}
          </Typography.Text>
          <DataTable<DataProcessor>
            rowKey="id"
            size="small"
            dataSource={processorsQ.data}
            loading={processorsQ.isLoading}
            columns={[
              { title: t('portal.dsgvo.proc.name', 'Anbieter'), dataIndex: 'name', key: 'name', width: 200 },
              { title: t('portal.dsgvo.proc.purpose', 'Zweck'), dataIndex: 'purpose', key: 'purpose' },
              { title: t('portal.dsgvo.proc.data', 'Datenkategorien'), dataIndex: 'data_categories',
                key: 'data', width: 220 },
              { title: t('portal.dsgvo.proc.country', 'Land'), dataIndex: 'country', key: 'country', width: 80,
                render: (v: string) => <Tag>{v}</Tag> },
              { title: '', key: 'avv', width: 80,
                render: (_, r) => r.avv_url ? (
                  <a href={r.avv_url} target="_blank" rel="noopener noreferrer">
                    {t('portal.dsgvo.proc.avv', 'AVV')}
                  </a>
                ) : null },
            ]}
            renderMobileCard={(r: DataProcessor) => (
              <div className="mob-card">
                <div className="flex-between mb-6">
                  <strong>{r.name}</strong>
                  <Tag>{r.country}</Tag>
                </div>
                <div className="text-sm mb-4" style={{ color: '#64748B' }}>{r.purpose}</div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  <span>{r.data_categories}</span>
                  {r.avv_url && <a href={r.avv_url} target="_blank" rel="noopener noreferrer">AVV</a>}
                </div>
              </div>
            )}
          />
          <Divider />
          <Typography.Text type="secondary" className="text-xs">
            {t('portal.dsgvo.processors.legal',
              'Mit jedem Auftragsverarbeiter besteht ein gültiger Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO.')}
          </Typography.Text>
        </Card>
      )}
    </>
  )
}
